import React, { useState } from "react";
import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { useSelector } from "react-redux";
import SweepPerformanceModal from "../../../../../components/Modals/SweepPerformanceModal/SweepPerformanceModal";
import { selectTeamQueryStats } from "../../../../../slices/teamStatsSlice";

const QuickStats = () => {
  const teamQueries = useSelector(selectTeamQueryStats);

  const [openSweepReportsModal, setOpenSweepReportsModal] = useState(false);

  const handleOpenSweepReportsModal = () => {
    setOpenSweepReportsModal(true);
  };

  const handleCloseSweepReportsModal = () => {
    setOpenSweepReportsModal(false);
  };

  const usedMonthlyQueries =
    teamQueries.subscriptionMonthlyQueries - teamQueries.remainingMonthlyQueries;

  const hasNoRemainingQueries = teamQueries.remainingMonthlyQueries === 0;

  return (
    <>
      <StatsShell>
        <StatsCard>
          <StatsHeader>
            <IconBadge>
              <QueryStatsIcon />
            </IconBadge>

            <div>
              <StatsEyebrow>Operations</StatsEyebrow>
              <StatsTitle>Record Sweeps</StatsTitle>
            </div>
          </StatsHeader>

          <StatsDivider />

          <StatsRow>
            <span>Last Record Sweep</span>
            <strong>{teamQueries.lastQuery}</strong>
          </StatsRow>

          <StatsRow>
            <span>Next Record Sweep</span>
            <strong>{teamQueries.nextQuery}</strong>
          </StatsRow>

          <ReportsButton type="button" onClick={handleOpenSweepReportsModal}>
            View sweep reports
            <OpenInNewIcon />
          </ReportsButton>

          <UsageBlock>
            <UsageHeader>Monthly Queries</UsageHeader>

            <StatsRow>
              <span>Used</span>
              <strong>{usedMonthlyQueries}</strong>
            </StatsRow>

            <StatsRow className={hasNoRemainingQueries ? "warning" : ""}>
              <span>Remaining</span>
              <strong>{teamQueries.remainingMonthlyQueries}</strong>
            </StatsRow>
          </UsageBlock>
        </StatsCard>
      </StatsShell>

      <Dialog
        open={openSweepReportsModal}
        onClose={handleCloseSweepReportsModal}
        PaperProps={{
          sx: {
            width: "121rem",
            minWidth: "121rem",
            maxWidth: "121rem",
            height: "55rem",
            minHeight: "55rem",
            maxHeight: "55rem",
            borderRadius: "0.8rem",
            overflow: "hidden",
          },
        }}
        tabIndex={-1}
      >
        <SweepPerformanceModal
          handleCloseModal={handleCloseSweepReportsModal}
          origin="quickStats"
        />
      </Dialog>
    </>
  );
};

const StatsHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "0.8rem",
});

const StatsShell = styled("section")({
  gridRow: 3,
  gridColumn: 1,
  minHeight: 0,
  overflow: "hidden",
  padding: "0.8rem",
  borderTop: "0.1rem solid rgba(120,139,158,0.24)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0.36))",
});

const StatsCard = styled("div")({
  height: "100%",
  minHeight: 0,
  overflow: "hidden",
  width: "100%",
  borderRadius: "0.8rem",
  padding: "1rem",
  background: "rgba(255,255,255,0.72)",
  border: "0.1rem solid rgba(120,139,158,0.24)",
  boxShadow: "0 0.8rem 1.6rem rgba(71,85,105,0.1)",
});

const IconBadge = styled("div")({
  width: "3.3rem",
  height: "3.3rem",
  borderRadius: "0.8rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#2563eb",
  background: "rgba(59,130,246,0.12)",
  border: "0.1rem solid rgba(59,130,246,0.2)",

  "& svg": {
    width: "2rem",
    height: "2rem",
  },
});

const StatsEyebrow = styled("div")({
  color: "#64748b",
  fontSize: "0.78rem",
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
});

const StatsTitle = styled("div")({
  color: "#1f2937",
  fontSize: "1.25rem",
  fontWeight: 950,
  letterSpacing: 0,
});

const StatsDivider = styled("div")({
  width: "100%",
  height: "0.1rem",
  margin: "0.5rem 0",
  background: "rgba(120,139,158,0.22)",
});

const StatsRow = styled("div")({
  minHeight: "1.8rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.8rem",

  "& span": {
    color: "#64748b",
    fontSize: "0.92rem",
    fontWeight: 700,
  },

  "& strong": {
    color: "#1f2937",
    fontSize: "0.95rem",
    fontWeight: 900,
    textAlign: "right",
  },

  "&.warning span, &.warning strong": {
    color: "#c2410c",
  },
});

const ReportsButton = styled("button")({
  width: "100%",
  height: "2.8rem",
  marginTop: "0.8rem",
  borderRadius: "0.7rem",
  border: "0.1rem solid rgba(59,130,246,0.24)",
  background: "rgba(59,130,246,0.1)",
  color: "#2563eb",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.45rem",
  fontSize: "0.9rem",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  cursor: "pointer",

  "& svg": {
    width: "1.4rem",
    height: "1.4rem",
  },

  "&:hover": {
    background: "rgba(59,130,246,0.16)",
    borderColor: "rgba(59,130,246,0.34)",
  },
});

const UsageBlock = styled("div")({
  marginTop: "0.9rem",
  paddingTop: "0.75rem",
  borderTop: "0.1rem solid rgba(120,139,158,0.22)",
});

const UsageHeader = styled("div")({
  marginBottom: "0rem",
  color: "#1f2937",
  fontSize: "0.95rem",
  fontWeight: 950,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
});

export default QuickStats;